import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionDtoStatusEnum,
  AssignIssueRequestDto,
  GetIssueTemplateByIssueTypeIssueSubtypeResponseDto,
  InitiateWorkflowDto,
  IssueResponseDtoStatusEnum,
  SaveActionRequestDto,
  SaveAttachmentDto,
  SaveIssueRequestDto,
  SLAViewDto,
} from '@issuemanager/api-client';
import {
  ControlledDateField,
  ControlledFileUploader,
  ControlledTextField,
  GovmapGroupsSelector,
  UserSelector,
} from '@issuemanager/apps/client/components';
import { useAttachmentsContext } from '@issuemanager/apps/client/contexts';
import {
  useAllMutations,
  useBadRequestOrConflictError,
  useDateTimeFormat,
  useMonitorManageIssueFormAssign,
  useResolutionOptions,
  useReviewStatusOptions,
} from '@issuemanager/apps/client/hooks';
import { useAtlasSemanticThemeTokens } from '@issuemanager/apps/client/theme';
import {
  useAssignIssue,
  useInitiateWorkflow,
  useIssueSeverities,
  useSaveIssue,
} from '@issuemanager/client/features/issues';
import { useSLAHelperText } from '@issuemanager/client/features/slas';
import { useFeatureFlags } from '@issuemanager/client/features/users';
import {
  ACTION_DESCRIPTION_LENGTH_MAX,
  ACTION_TITLE_LENGTH_MAX,
  ISSUE_DESCRIPTION_LENGTH_MAX,
  ISSUE_IMPACT_LENGTH_MAX,
  ISSUE_TITLE_LENGTH_MAX,
  ISSUE_WORKFLOW_EXECUTION_TYPE,
  ISSUE_WORKFLOW_EXECUTION_TYPE_VALUES,
  IssueClassificationEnum,
  IssuePermissionsEnum,
  IssueStatusEnum,
  IssueTimelineEventTypeEnum,
  MANAGEMENT_RESPONSE_LENGTH_MAX,
  RECOMMENDATIONS_LENGTH_MAX,
  ROOT_CAUSE_LENGTH_MAX,
  UserQueryMode,
} from '@issuemanager/constants';
import { getIssueStates } from '@issuemanager/models';
import {
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  useEventCallback,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Controller,
  FieldError,
  FieldErrorsImpl,
  Merge,
  Resolver,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { AttachmentsList } from '../components/action-details/attachments-list';
import {
  getManageIssueFormSaveSchema,
  ManageIssueFormSaveSchemaType,
} from '../schemas/manage-issue-form-save-schema';
import {
  getManageIssueFormSubmitSchema,
  ManageIssueFormSubmitSchemaType,
} from '../schemas/manage-issue-form-submit-schema';
import {
  getSLAMaxDateTime,
  isDateEarlierThanSLA,
} from '../utils/sla-validation';
import { splitFormData } from '../utils/split-issue-form-data';
import { useActionRefs } from './use-action-refs';
import { useActionValidationHandler } from './use-action-validation-handler';
import { useFieldState } from './use-field-state';
import { useIssueStatusInfo } from './use-issue-status-info';
import { useIssueOutletContext } from './useIssueOutletContext';

export type ActionFieldReturnType = {
  id: string;
  handleDelete: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  expanded: boolean;
  handleExpand: () => void;
  title: ReactNode;
  description: ReactNode;
  dueDate: ReactNode;
  ownerId: ReactNode;
  readOnly: boolean;
  isInProgress: boolean;
  isOpen: boolean;
  isToBeApproved: boolean;
  approverId: ReactNode;
  sequence: number | null;
  isPending: boolean;
  isDraft: boolean;
  attachments: ReactNode | null;
  errors?: Merge<FieldError, FieldErrorsImpl>;
  ref?: (node: HTMLDivElement) => void;
};

export type ActionField = SaveActionRequestDto & {
  customId: string;
};

type Attachment = SaveAttachmentDto & {
  file: File;
};

export type AssignIssueFormValues = SaveIssueRequestDto & {
  actions: ActionField[];
  attachments?: Attachment[];
};

export interface ManageIssueFormParams {
  SLAs: SLAViewDto[];
  issueTemplate?: GetIssueTemplateByIssueTypeIssueSubtypeResponseDto;
  readOnly: boolean;
  onSubmitSuccess: () => void;
}

export const useManageIssueForm = ({
  SLAs,
  issueTemplate,
  readOnly,
  onSubmitSuccess,
}: ManageIssueFormParams) => {
  const { t } = useTranslation(['common', 'issue', 'action']);
  const { timezoneOffset } = useDateTimeFormat();
  const { code = '' } = useParams();
  const { setErrors: setServiceErrors } = useBadRequestOrConflictError();
  const { isLoading: isUploadAttachmentsLoading } = useAttachmentsContext();
  const { issue, userRoles: issueUserRoles } = useIssueOutletContext();
  const isIssueTypesExtraFieldsEnabled = Boolean(
    useFeatureFlags('issueTypesExtraFields') ?? false,
  );
  const { isNotYetAssigned, isInitiated } = useIssueStatusInfo({
    status: issue.data?.status,
    isInitiated: issue.data?.isInitiated,
    approvalStatus: issue.data?.approvalStatus,
  });
  const { enqueueSnackbar } = useSnackbar();
  const semantic = useAtlasSemanticThemeTokens();

  const monitorManageIssueFormAssign = useMonitorManageIssueFormAssign();
  const assignIssue = useAssignIssue(code, setServiceErrors);
  const initiateWorkflow = useInitiateWorkflow({
    code,
  });

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [operation, setOperation] = useState<string>('');

  useAllMutations({
    mutations: [assignIssue],
    onStart: () => {
      monitorManageIssueFormAssign.start();
    },
    onSuccess: () => {
      monitorManageIssueFormAssign.stop();
    },
    onError: () => {
      monitorManageIssueFormAssign.error();
    },
  });

  const isIssueOpen = issue.data?.status === IssueStatusEnum.OPEN;
  const isDraftIssue = issue.data?.status === IssueStatusEnum.DRAFT;

  const [schema, setSchema] = useState<
    ManageIssueFormSaveSchemaType | ManageIssueFormSubmitSchemaType
  >(
    getManageIssueFormSaveSchema({
      t,
      timezoneOffset,
      SLAs,
      isDateEarlierThanSLA,
      issueStatus: issue.data?.status,
      issueActionProgress: issue.data?.actionProgress,
      isDraftIssue,
      isIssueTypesExtraFieldsEnabled,
    }),
  );

  const form = useForm<AssignIssueFormValues>({
    mode: 'all',
    criteriaMode: 'all',
    shouldFocusError: false, // We handle focus manually with scrollIntoView
    defaultValues: {
      issueType: issue.data?.issueType?.name,
      executionType: ISSUE_WORKFLOW_EXECUTION_TYPE.SEQUENTIAL,
      actions: [],
    },
    resolver: zodResolver(schema) as Resolver<AssignIssueFormValues>,
  });

  const actionsFieldArray = useFieldArray<AssignIssueFormValues, 'actions'>({
    name: 'actions',
    control: form.control,
  });

  const { actionRefs, registerActionRef } = useActionRefs();
  const { handleValidationErrors } = useActionValidationHandler({
    actionsFieldArray,
    expanded,
    setExpanded,
    actionRefs,
  });

  const [
    currentOwnerId,
    currentApproverId,
    currentIssueDueDate,
    currentActions,
    currentManagerId,
    currentExecutionType,
    currentSeverity,
  ] = form.watch([
    'ownerId',
    'approverId',
    'dueDate',
    'actions',
    'managerId',
    'executionType',
    'severity',
  ]);

  const onSubmit = useEventCallback(async (): Promise<void> => {
    if (isUploadAttachmentsLoading) {
      enqueueSnackbar(t('issue:form.attachments.uploadInProgress.message'), {
        variant: 'info',
      });
      return;
    }

    const values: AssignIssueFormValues = form.getValues();

    const { approverId = null, dueDate, actions, ...rest } = values;
    const actionsWithSequenceUpdated = actions.map((action, index) => ({
      ...action,
      title: action.title?.trim(),
      sequence: index,
    }));

    if (!dueDate) {
      // This is for the unlikely case that somehow all validation and datepicker
      // component logic fails.
      enqueueSnackbar(t('issue:form.errorMessages.dueDate.required'), {
        variant: 'error',
      });
      return;
    }

    const attachments = values.attachments?.map((attachment) => ({
      name: attachment.name,
      size: attachment.size,
      hbDriveId: attachment.hbDriveId,
      id: attachment.id,
    }));

    if (isNotYetAssigned) {
      await assignIssue.mutateAsync({
        approverId,
        // TODO: this is a temporary fix to avoid the issue due date being set to the past
        // Ticket for date serialization fix: https://diligentbrands.atlassian.net/browse/RCP-23573
        dueDate,
        actions: actionsWithSequenceUpdated,
        ...rest,
        attachments,
        selectedTemplateId: issueTemplate?.id,
      } as AssignIssueRequestDto);
    } else {
      // TODO: ignore ts temporarily, need to fix this when we support issue data and actions
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      await initiateWorkflow.mutateAsync({
        ...rest,
        approverId,
        dueDate,
        attachments,
        actions: actionsWithSequenceUpdated,
      } as InitiateWorkflowDto);
    }
    onSubmitSuccess();
  });

  const handleSubmit = useEventCallback(
    (submitCallback?: () => Promise<void>) => {
      setSchema(
        getManageIssueFormSubmitSchema({
          t,
          timezoneOffset,
          SLAs,
          isDateEarlierThanSLA,
          issueStatus: issue.data?.status,
          issueActionProgress: issue.data?.actionProgress,
          isDraftIssue,
          isIssueTypesExtraFieldsEnabled,
        }),
      );

      setTimeout(async () => {
        if (isUploadAttachmentsLoading) {
          enqueueSnackbar(
            t('issue:form.attachments.uploadInProgress.message'),
            { variant: 'info' },
          );
          return;
        }

        const callback =
          submitCallback && typeof submitCallback === 'function'
            ? async () => {
                await submitCallback();
              }
            : onSubmit;
        await form.handleSubmit(callback, handleValidationErrors)();
        // temporary fix for this random occurring bug: https://diligentbrands.atlassian.net/browse/RCP-19633
      }, 10);
    },
  );

  const handleSave = useEventCallback(
    (saveIssue: ReturnType<typeof useSaveIssue>) => {
      setSchema(
        getManageIssueFormSaveSchema({
          t,
          timezoneOffset,
          SLAs,
          isDateEarlierThanSLA,
          issueStatus: issue.data?.status,
          issueActionProgress: issue.data?.actionProgress,
          isDraftIssue,
          isIssueTypesExtraFieldsEnabled,
        }),
      );

      setTimeout(async () => {
        if (isUploadAttachmentsLoading) {
          enqueueSnackbar(
            t('issue:form.attachments.uploadInProgress.message'),
            { variant: 'info' },
          );
          return;
        }

        const onValidSubmit = async (): Promise<void> => {
          const values = form.getValues();

          const { baseValues, typeSpecificValues } = splitFormData(
            values,
            values.issueType,
            'edit',
          );

          await saveIssue.mutateAsync({
            ...baseValues,
            ...typeSpecificValues,
            issueType: values.issueType,
            actions: values.actions.map((action, index) => ({
              ...action,
              title: action.title?.trim(),
              sequence: index,
            })),
            selectedTemplateId:
              issue.data?.selectedTemplate ?? issueTemplate?.id,
          });
        };

        await form.handleSubmit(onValidSubmit, handleValidationErrors)();
        // temporary fix for this random occurring bug: https://diligentbrands.atlassian.net/browse/RCP-19633
      }, 10);
    },
  );

  const handleAddAction = useEventCallback(() => {
    if (issue.data?.status === IssueResponseDtoStatusEnum.ToBeApproved) return;
    actionsFieldArray.append({
      title: '',
      description: '',
      dueDate: null,
      ownerId: '',
      sequence: actionsFieldArray.fields.length,
      status: ActionDtoStatusEnum.Draft,
      customId: uuidv4(),
      attachments: [],
    });
    setOperation('append');
  });

  const handleExpand = useEventCallback((id: string) => {
    if (expanded[id]) {
      setExpanded((prevExpanded) => ({ ...prevExpanded, [id]: false }));
    } else {
      setExpanded((prevExpanded) => ({ ...prevExpanded, [id]: true }));
    }
  });

  const handleDelete = useEventCallback(
    (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      index: number,
      customId: string,
    ) => {
      e.stopPropagation();

      setExpanded((prevExpanded) => {
        const newExpanded = { ...prevExpanded };
        delete newExpanded[customId];
        return newExpanded;
      });
      actionsFieldArray.remove(index);

      // NOTE: we need to force the form.actions to re-run validations over the whole array.
      // If not, error will remain after another change.
      // https://github.com/react-hook-form/react-hook-form/issues/1834
      form.trigger('actions');
    },
  );

  useEffect(() => {
    if (operation === 'append') {
      const lastAction =
        actionsFieldArray.fields[actionsFieldArray.fields.length - 1];
      handleExpand(lastAction.customId);
      setOperation('');
    }
  }, [operation, actionsFieldArray.fields, handleExpand]);

  const labels = useMemo(
    () => ({
      title: t('issue:form.labels.title'),
      description: t('issue:form.labels.description'),
      ownerId: t('issue:form.labels.owner'),
      approverId: t('issue:form.labels.approver'),
      severity: t('issue:form.labels.severity'),
      dueDate: t('issue:form.labels.dueDate'),
      relatedOrgUnit: t('issue:form.labels.relatedOrgUnit'),
      managerId: t('issue:form.labels.manager'),
      impact: t('issue:form.labels.impact'),
      classification: t('issue:form.labels.classification'),
      rootCause: t('issue:form.labels.rootCause'),
      recommendations: t('issue:form.labels.recommendations'),
      managementResponse: t('issue:form.labels.managementResponse'),
      reviewStatus: t('issue:form.labels.reviewStatus'),
      datePublished: t('issue:form.labels.datePublished'),
      resolution: t('issue:form.labels.resolution'),
      remediation: t('issue:form.labels.remediation'),
      includeInAuditReport: t('issue:form.labels.includeInAuditReport'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const actionLabels = useMemo(
    () => ({
      title: t('action:form.labels.title'),
      description: t('action:form.labels.description'),
      dueDate: t('action:form.labels.dueDate'),
      ownerId: t('action:form.labels.owner'),
      approverId: t('action:form.labels.approver'),
      attachments: t('action:form.labels.attachments'),
      // NOTE: related entities will be used in the future so far are not needed. Ticket RCP-12663
      // entities: t('action:form.labels.entities'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const issueStates = getIssueStates(
    issue.data?.status
      ? { actionProgress: issue.data.actionProgress }
      : undefined,
  );

  const updateApproverOnOwnerChange = (ownerId: string | null): void => {
    if (!form.watch('approverId') && ownerId) {
      form.setValue('approverId', ownerId);
      form.trigger('approverId');
    }
  };

  const severityValues = useIssueSeverities(false).map((severity) => ({
    label: severity.label,
    value: severity.value as string,
  }));

  const maxIssueDueDate = useMemo(() => {
    if (!currentSeverity || SLAs.length === 0) {
      return undefined;
    }

    const isSeverityChanging = currentSeverity !== issue.data?.severity;

    if (
      issue.data?.slaEvent?.eventType ===
        IssueTimelineEventTypeEnum.SLADeactivated &&
      !isSeverityChanging
    ) {
      return undefined;
    }

    return getSLAMaxDateTime(SLAs, currentSeverity, issue.data?.createdAt);
  }, [
    currentSeverity,
    SLAs,
    issue.data?.createdAt,
    issue.data?.slaEvent,
    issue.data?.severity,
  ]);

  const slaHelperText = useSLAHelperText({
    SLAs,
    severity: currentSeverity,
    slaEvent: issue.data?.slaEvent,
    issueSeverity: issue.data?.severity,
  });

  const classificationValues = Object.values(IssueClassificationEnum).map(
    (value) => ({
      label: t(`common:issueClassification.${value}`) as string,
      value,
    }),
  );

  const reviewStatusValues = useReviewStatusOptions();
  const resolutionValues = useResolutionOptions();

  const impactState = useFieldState({
    issue: issue.data,
    userRoles: issueUserRoles.data,
    fieldName: 'impact',
    isReadOnly: readOnly,
  });

  const titleState = useFieldState({
    issue: issue.data,
    userRoles: issueUserRoles.data,
    fieldName: 'title',
    isReadOnly: readOnly,
  });

  const descriptionState = useFieldState({
    issue: issue.data,
    userRoles: issueUserRoles.data,
    fieldName: 'description',
    isReadOnly: readOnly,
  });

  const managerIdState = useFieldState({
    issue: issue.data,
    userRoles: issueUserRoles.data,
    fieldName: 'managerId',
    isReadOnly: readOnly,
  });

  const approverIdState = useFieldState({
    issue: issue.data,
    userRoles: issueUserRoles.data,
    fieldName: 'approverId',
    isReadOnly: readOnly,
  });

  const ownerIdState = useFieldState({
    issue: issue.data,
    userRoles: issueUserRoles.data,
    fieldName: 'ownerId',
    isReadOnly: readOnly,
  });

  const severityState = useFieldState({
    issue: issue.data,
    userRoles: issueUserRoles.data,
    fieldName: 'severity',
    isReadOnly: readOnly,
  });

  const dueDateState = useFieldState({
    issue: issue.data,
    userRoles: issueUserRoles.data,
    fieldName: 'dueDate',
    isReadOnly: readOnly,
  });

  const govmapGroupsState = useFieldState({
    issue: issue.data,
    userRoles: issueUserRoles.data,
    fieldName: 'govmapGroups',
    isReadOnly: readOnly,
  });

  const classificationState = useFieldState({
    issue: issue.data,
    userRoles: issueUserRoles.data,
    fieldName: 'classification',
    isReadOnly: readOnly,
  });

  const reviewStatusState = useFieldState({
    issue: issue.data,
    userRoles: issueUserRoles.data,
    fieldName: 'reviewStatus',
    isReadOnly: readOnly,
  });

  const datePublishedState = useFieldState({
    issue: issue.data,
    userRoles: issueUserRoles.data,
    fieldName: 'datePublished',
    isReadOnly: readOnly,
  });

  const rootCauseState = useFieldState({
    issue: issue.data,
    userRoles: issueUserRoles.data,
    fieldName: 'rootCause',
    isReadOnly: readOnly,
  });

  const recommendationsState = useFieldState({
    issue: issue.data,
    userRoles: issueUserRoles.data,
    fieldName: 'recommendations',
    isReadOnly: readOnly,
  });

  const managementResponseState = useFieldState({
    issue: issue.data,
    userRoles: issueUserRoles.data,
    fieldName: 'managementResponse',
    isReadOnly: readOnly,
  });

  const resolutionState = useFieldState({
    issue: issue.data,
    userRoles: issueUserRoles.data,
    fieldName: 'resolution',
    isReadOnly: readOnly,
  });

  const remediationState = useFieldState({
    issue: issue.data,
    userRoles: issueUserRoles.data,
    fieldName: 'remediation',
    isReadOnly: readOnly,
  });

  const includeInAuditReportState = useFieldState({
    issue: issue.data,
    userRoles: issueUserRoles.data,
    fieldName: 'includeInAuditReport',
    isReadOnly: readOnly,
  });

  const issueFields = {
    title: (
      <ControlledTextField
        name='title'
        label={labels.title}
        control={form.control}
        rules={{
          required: t('issue:form.errorMessages.title.required'),
        }}
        maxLength={ISSUE_TITLE_LENGTH_MAX}
        inputProps={{
          readOnly: !titleState.editable,
          required: titleState.required,
        }}
      />
    ),
    description: (
      <ControlledTextField
        name='description'
        label={labels.description}
        control={form.control}
        minRows={4}
        multiline
        maxLength={ISSUE_DESCRIPTION_LENGTH_MAX}
        inputProps={{
          readOnly: !descriptionState.editable,
          required: descriptionState.required,
        }}
      />
    ),
    ownerId: (
      <Controller
        name='ownerId'
        control={form.control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          const handleOwnerChange = (newValue: string | null): void => {
            onChange(newValue);
            updateApproverOnOwnerChange(newValue);
          };
          return (
            <UserSelector
              value={value}
              initialValue={currentOwnerId}
              onChange={handleOwnerChange}
              readOnly={!ownerIdState.editable}
              TextFieldProps={{
                label: labels.ownerId,
                'data-testid': 'ownerId',
                required: ownerIdState.required,
                error: !!error?.message,
                helperText: error?.message,
                inputProps: {
                  'data-testid': 'ownerId-input',
                },
              }}
            />
          );
        }}
      />
    ),
    approverId: (
      <Controller
        name='approverId'
        control={form.control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <UserSelector
            value={value}
            initialValue={currentApproverId}
            onChange={onChange}
            readOnly={!approverIdState.editable}
            TextFieldProps={{
              label: labels.approverId,
              'data-testid': 'approverId',
              error: !!error?.message,
              helperText: error?.message,
              required: approverIdState.required,
              inputProps: {
                'data-testid': 'approverId-input',
              },
            }}
          />
        )}
      />
    ),
    severity: (
      <ControlledTextField
        name='severity'
        label={labels.severity}
        control={form.control}
        inputProps={{
          readOnly: !severityState.editable,
          required: severityState.required,
        }}
        options={severityValues}
      />
    ),
    dueDate: (
      <ControlledDateField
        name='dueDate'
        label={labels.dueDate}
        labelInfo={slaHelperText}
        control={form.control}
        disablePast
        maxDateTime={maxIssueDueDate}
        readOnly={!dueDateState.editable}
        onChange={() => {
          // NOTE: we need to force the form.dueDate to re-run validations over the whole array.
          // If not, error will remain after another change.
          actionsFieldArray.fields.forEach((_, i) => {
            form.trigger(`actions.${i}.dueDate`);
          });
        }}
        InputProps={{
          required: dueDateState.required,
        }}
      />
    ),
    govmapGroups: (
      <Controller
        name='govmapGroups'
        control={form.control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <GovmapGroupsSelector
            value={value ?? []}
            onChange={onChange}
            label={labels.relatedOrgUnit}
            error={!!error?.message}
            helperText={error?.message}
            readOnly={!govmapGroupsState.editable}
          />
        )}
      />
    ),
    impact: (
      <ControlledTextField
        name='impact'
        label={labels.impact}
        control={form.control}
        minRows={4}
        multiline
        maxLength={ISSUE_IMPACT_LENGTH_MAX}
        inputProps={{
          readOnly: !impactState.editable,
        }}
      />
    ),
    managerId: (
      <Controller
        name='managerId'
        control={form.control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <UserSelector
            value={value}
            initialValue={currentManagerId}
            onChange={onChange}
            readOnly={!managerIdState.editable}
            queryMode={UserQueryMode.ONLY_ISSUE_TYPE_MANAGERS}
            issueTypeId={issue.data?.issueType?.id}
            issueSubtypeId={issue.data?.issueSubtype?.id}
            TextFieldProps={{
              label: labels.managerId,
              'data-testid': 'managerId',
              required: managerIdState.required,
              error: !!error?.message,
              helperText: error?.message,
              inputProps: {
                'data-testid': 'managerId-input',
              },
            }}
          />
        )}
      />
    ),
    executionType: (
      <Controller
        name='executionType'
        control={form.control}
        render={({ field: { value, onChange } }) => (
          <RadioGroup
            name='execution-type'
            value={value}
            onChange={(args) => {
              onChange(args);
              actionsFieldArray.fields.forEach((_, i) => {
                form.trigger(`actions.${i}.dueDate`);
              });
            }}
            sx={{
              gap: 2,
              '.MuiRadio-root': {
                padding: '3px',
                '&.Mui-disabled': {
                  color: '#676767',
                },
              },
              '&& .MuiFormControlLabel-label': {
                '&.Mui-disabled': {
                  color: '#676767',
                },
              },
            }}
          >
            {ISSUE_WORKFLOW_EXECUTION_TYPE_VALUES.map((type) => (
              <FormControlLabel
                key={type}
                value={type}
                control={
                  <Radio
                    disabled={readOnly || issueStates.isWorkflowInitiated}
                  />
                }
                label={t(`issue:form.labels.executionType.${type}`)}
              />
            ))}
          </RadioGroup>
        )}
      />
    ),
  };

  const extraFields = isIssueTypesExtraFieldsEnabled
    ? {
        classification: (
          <ControlledTextField
            name='classification'
            label={labels.classification}
            data-testid='classification'
            control={form.control}
            options={classificationValues}
            inputProps={{
              readOnly: !classificationState.editable,
              required: classificationState.required,
            }}
          />
        ),
        reviewStatus: (
          <ControlledTextField
            name='reviewStatus'
            label={labels.reviewStatus}
            data-testid='reviewStatus'
            control={form.control}
            options={reviewStatusValues}
            inputProps={{
              readOnly: !reviewStatusState.editable,
            }}
          />
        ),
        datePublished: (
          <ControlledDateField
            name='datePublished'
            label={labels.datePublished}
            control={form.control}
            readOnly={!datePublishedState.editable}
            data-testid='datePublished'
          />
        ),
        resolution: (
          <ControlledTextField
            name='resolution'
            label={labels.resolution}
            data-testid='resolution'
            control={form.control}
            options={resolutionValues}
            inputProps={{
              readOnly: !resolutionState.editable,
            }}
          />
        ),
        rootCause: (
          <ControlledTextField
            name='rootCause'
            label={labels.rootCause}
            control={form.control}
            data-testid='rootCause'
            minRows={4}
            multiline
            maxLength={ROOT_CAUSE_LENGTH_MAX}
            inputProps={{
              readOnly: !rootCauseState.editable,
              required: rootCauseState.required,
            }}
          />
        ),
        recommendations: (
          <ControlledTextField
            name='recommendations'
            label={labels.recommendations}
            control={form.control}
            data-testid='recommendations'
            minRows={4}
            multiline
            maxLength={RECOMMENDATIONS_LENGTH_MAX}
            inputProps={{
              readOnly: !recommendationsState.editable,
              required: recommendationsState.required,
            }}
          />
        ),
        managementResponse: (
          <ControlledTextField
            name='managementResponse'
            label={labels.managementResponse}
            control={form.control}
            data-testid='managementResponse'
            minRows={4}
            multiline
            maxLength={MANAGEMENT_RESPONSE_LENGTH_MAX}
            inputProps={{
              readOnly: !managementResponseState.editable,
            }}
          />
        ),
        remediation: (
          <ControlledTextField
            name='remediation'
            label={labels.remediation}
            control={form.control}
            data-testid='remediation'
            minRows={4}
            multiline
            maxLength={MANAGEMENT_RESPONSE_LENGTH_MAX}
            inputProps={{
              readOnly: !remediationState.editable,
            }}
          />
        ),
        includeInAuditReport: (
          <Controller
            name='includeInAuditReport'
            control={form.control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(e) => onChange(e.currentTarget.checked)}
                    checked={Boolean(value)}
                    data-testid='includeInAuditReport-checkbox'
                    disabled={!includeInAuditReportState.editable}
                  />
                }
                label={labels.includeInAuditReport}
              />
            )}
          />
        ),
      }
    : {};

  const actionFields = useMemo(
    () =>
      actionsFieldArray.fields.map((field, index): ActionFieldReturnType => {
        const actionErrors = form.formState.errors.actions?.[index];

        const readOnlyStatuses: ActionDtoStatusEnum[] = [
          ActionDtoStatusEnum.Completed,
          ActionDtoStatusEnum.Discarded,
        ];

        const readOnlyAction =
          field.id && field.status
            ? readOnlyStatuses.includes(field.status)
            : false;

        const canAddAttachments = Boolean(
          !issue.data?.manager ||
            issueUserRoles.data?.isManager ||
            issueUserRoles.data?.isOwner ||
            issueUserRoles.data?.isIssueTypeManager,
        );

        const hideRemoveAttachmentButton = Boolean(
          (issueUserRoles.data?.isOwner || issueUserRoles.data?.isManager) &&
            isInitiated,
        );

        const actionAttachments = issue.data?.actions[index]?.attachments;

        return {
          id: field.id,
          handleDelete: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
            handleDelete(e, index, field.customId),
          expanded: expanded[field.customId] ?? false,
          handleExpand: () => handleExpand(field.customId),
          readOnly: readOnlyAction,
          isInProgress: field.status === ActionDtoStatusEnum.InProgress,
          isOpen: field.status === ActionDtoStatusEnum.Open,
          isToBeApproved: field.status === ActionDtoStatusEnum.ToBeApproved,
          isDraft: field.status === ActionDtoStatusEnum.Draft,
          isPending: field.status === ActionDtoStatusEnum.Pending,
          sequence: field.sequence ?? null,
          errors: actionErrors,
          ref: registerActionRef(field.customId),
          title: (
            <ControlledTextField
              name={`actions.${index}.title`}
              label={actionLabels.title}
              labelInfo={t('action:form.info.title')}
              control={form.control}
              data-testid='title'
              maxLength={ACTION_TITLE_LENGTH_MAX}
              inputProps={{
                readOnly: readOnlyAction,
                required: true,
              }}
            />
          ),
          description: (
            <ControlledTextField
              name={`actions.${index}.description`}
              label={actionLabels.description}
              control={form.control}
              minRows={4}
              multiline
              data-testid='description'
              inputProps={{
                readOnly: readOnlyAction,
                required: !isIssueOpen,
              }}
              maxLength={ACTION_DESCRIPTION_LENGTH_MAX}
            />
          ),
          dueDate: (
            <ControlledDateField
              name={`actions.${index}.dueDate`}
              label={actionLabels.dueDate}
              control={form.control}
              disablePast
              maxDateTime={
                currentIssueDueDate ? new Date(currentIssueDueDate) : undefined
              }
              data-testid='dueDate'
              readOnly={readOnlyAction}
              InputProps={{
                required: !isIssueOpen,
              }}
              onChange={() => {
                // NOTE: we need to force the form.dueDate to re-run validations
                // over the whole array. If not, error will remain after another change
                actionsFieldArray.fields.forEach((_, i) => {
                  form.trigger(`actions.${i}.dueDate`);
                });
              }}
            />
          ),
          ownerId: (
            <Controller
              name={`actions.${index}.ownerId`}
              control={form.control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <UserSelector
                  initialValue={field.ownerId}
                  value={value}
                  onChange={onChange}
                  readOnly={readOnlyAction}
                  TextFieldProps={{
                    label: actionLabels.ownerId,
                    'data-testid': 'ownerId',
                    required: !isIssueOpen,
                    error: !!error?.message,
                    helperText: error?.message,
                    inputProps: {
                      'data-testid': 'ownerId-input',
                    },
                  }}
                />
              )}
            />
          ),
          approverId: (
            <Controller
              name={`actions.${index}.approverId`}
              control={form.control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <UserSelector
                  initialValue={field.approverId}
                  value={value}
                  readOnly={readOnlyAction}
                  onChange={onChange}
                  TextFieldProps={{
                    label: actionLabels.approverId,
                    'data-testid': 'approverId',
                    error: !!error?.message,
                    helperText: error?.message,
                    inputProps: {
                      'data-testid': 'approverId-input',
                    },
                  }}
                />
              )}
            />
          ),
          attachments: (
            <>
              {canAddAttachments && (
                <ControlledFileUploader
                  control={form.control}
                  label={actionLabels.attachments}
                  name={`actions.${index}.attachments`}
                  fullWidth={false}
                  marginRight={semantic.spacing['2xl'].value}
                  permissions={[
                    IssuePermissionsEnum.IsOwner,
                    IssuePermissionsEnum.IsManager,
                    IssuePermissionsEnum.IsIssueTypeManager,
                  ]}
                  hideDeleteButton={hideRemoveAttachmentButton}
                />
              )}
              {!canAddAttachments && actionAttachments && (
                <AttachmentsList attachments={actionAttachments} />
              )}
            </>
          ),
          /*
          NOTE: related entities will be used in the future so far are not needed. Ticket RCP-12663
          govmapGroups: (
            <Controller
              name={`actions.${index}.govmapGroups`}
              control={initiateWorkflowForm.control}
              render={({ field: { value, onChange }, fieldState: { error } }) => {
                return (
                  <GovmapGroupsSelector
                    value={value}
                    onChange={onChange}
                    label={labels.entities}
                    error={!!error?.message}
                    helperText={error?.message}
                  />
                );
              }}
            />
          ),
        */
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      actionsFieldArray,
      expanded,
      currentIssueDueDate,
      registerActionRef,
      form,
      handleDelete,
      handleExpand,
      isInitiated,
      isIssueOpen,
      !isInitiated,
      issue.dataUpdatedAt,
      issueUserRoles.dataUpdatedAt,
      actionLabels,
    ],
  );

  return {
    manageIssueForm: form,
    issueFields: {
      ...issueFields,
      ...extraFields,
    },
    actionFields,
    assignIssueIsLoading: assignIssue.isPending,
    initiateWorkflowIsLoading: initiateWorkflow.isPending,
    watched: {
      executionType:
        currentExecutionType || ISSUE_WORKFLOW_EXECUTION_TYPE.PARALLEL,
      actions: currentActions,
    },
    manageIssueHandleSubmit: handleSubmit,
    manageIssueHandleSave: handleSave,
    onAddAction: handleAddAction,
  };
};
